<template>
  <v-container grid-list-md fluid pa-0>
    <v-layout wrap>
      <v-flex xs12 md4>
        <a ref="wizard-top" />
        <template v-for="(header, index) in headers">
          <div :key="index" class="step">
            <h2
              v-if="stepNumber === index"
              :class="[stepNumber === index ? 'selected' : '']"
            >
              {{ index + 1 }}. {{ header.name }}
            </h2>
            <h3
              v-else
              :class="{ 'bm-wizard-clickable': index !== stepNumber }"
              @click="() => goToPage(index)"
            >
              {{ index + 1 }}. {{ header.name }}
            </h3>
            <div v-show="stepNumber === index">{{ header.description }}</div>
          </div>
        </template>
      </v-flex>
      <v-flex xs12 md8>
        <slot />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import { WizardInputEventType } from "@/components/controls/wizard/WizardEvents";
import wizardStore from "@/store/modules/wizardStore";
import { getScrollParent } from "@/app/ui/utilities";
import { defineComponent } from "vue";

class StepHeading {
  public name: string;
  public description: string;
  public optional: boolean;

  public constructor(name: string, description: string, optional: boolean) {
    this.name = name;
    this.description = description;
    this.optional = optional;
  }
}

export default defineComponent({
  name: "BmWizard",
  data() {
    return {
      headers: [] as StepHeading[],
      steps: [] as any[],
    };
  },
  computed: {
    stepNumber(): number {
      return wizardStore.state.stepNumber;
    },
  },
  watch: {
    stepNumber: {
      // @ts-ignore
      handler: "stepNumberWatch",
      immediate: false,
    },
  },
  mounted() {
    this.refreshSteps();

    this.goToPage(0);

    this.stepNumberWatch(this.stepNumber, this.stepNumber);

    this.$root.$on(WizardInputEventType.NextStep, this.nextStep);
    this.$root.$on(WizardInputEventType.PreviousStep, this.previousStep);
    this.$root.$on(WizardInputEventType.Close, this.closeWizard);
    this.$root.$on(WizardInputEventType.Finished, this.finished);
  },
  destroyed(): void {
    this.$root.$off(WizardInputEventType.NextStep, this.nextStep);
    this.$root.$off(WizardInputEventType.PreviousStep, this.previousStep);
    this.$root.$off(WizardInputEventType.Close, this.closeWizard);
    this.$root.$off(WizardInputEventType.Finished, this.finished);
  },
  methods: {
    goToPage(newStep: number) {
      const currentStep = this.steps[this.stepNumber];

      if (newStep > this.stepNumber) {
        // going forward requires this step to validate
        if (!currentStep.validate()) {
          return;
        }
      }

      // Check if any of the remaining steps (not including the current) are non-optional
      const atLastRequiredStep = !this.headers
        .slice(newStep + 1)
        .some((step) => !step.optional);

      wizardStore.updateStep(newStep, atLastRequiredStep);
    },
    refreshSteps() {
      this.steps = [];
      this.headers = [];
      this.$children
        .filter((c: any) => c.$options.name === "BmWizardStep" && c.enabled)
        .forEach((c: any) => {
          this.steps.push(c);
          this.headers.push({
            name: c.title,
            description: c.description,
            optional: c.optional,
          });
        });
      wizardStore.setNumberOfSteps(this.steps.length);
    },
    nextStep() {
      const newStep: number =
        this.stepNumber < this.steps.length - 1
          ? this.stepNumber + 1
          : this.stepNumber;
      this.goToPage(newStep);
    },
    previousStep() {
      const newStep: number =
        this.stepNumber > 0 ? this.stepNumber - 1 : this.stepNumber;
      this.goToPage(newStep);
    },
    closeWizard() {
      const newStep: number = this.stepNumber + 1;
      this.$emit("change", newStep);

      // Emit a close event if we have listeners, otherwise head back to where we were before we got here.
      // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
      this.$listeners.close
        ? this.$emit("close")
        : this.$router.backWithFallback();
    },
    finished() {
      for (let idx = 0; idx < this.steps.length; idx++) {
        if (!this.steps[idx].validate()) {
          this.goToPage(idx);
          return;
        }
      }

      // TODO: is submit best here??
      this.$emit("submit");
    },
    stepNumberWatch(val: number, oldVal: number) {
      if (this.steps[oldVal]) {
        this.steps[oldVal].isVisible = false;
      }
      this.steps[val].isVisible = true;

      // Scroll to the top of the page
      if (this.$refs["wizard-top"] !== undefined) {
        const top = this.$refs["wizard-top"] as any;

        this.$vuetify
          .goTo(top, {
            offset: 100,
            container: getScrollParent(top, false),
          })
          .then();
      }
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.bm-wizard-clickable
  color: $bm-color-brand02
  cursor: pointer

.selected
  color: $bm-color-brand03

.step
  margin-bottom: 30px
</style>
